import {ComputedRef, ref} from "vue";
import {medicationCheckupApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import fileHelper from "@/core/helpers/fileHelper";

/**
 * A document a closing serves, as the scene hands it: rendered on request
 * by the HDS from the frozen record, so the wait is the generation itself.
 */
interface ClosingDocument {
    generated_at: string;
    filename?: string;
    url: string;
}

/**
 * The name a served document is saved or attached under: the one the HDS
 * files it under, else the given name dated with the validation.
 *
 * @param document The document the scene serves.
 * @param name The name of the file, before its date, when the HDS serves none.
 */
export function closingDocumentFilename(document: ClosingDocument, name: string): string {
    return document.filename ?? `${name}-${document.generated_at.slice(0, 10)}.pdf`;
}

/**
 * The download of the form a closing serves, saved under the name the HDS
 * files it under, and whether it is being fetched.
 *
 * @param document The document the scene serves, null until validated.
 * @param name The name of the file, before its date, when the HDS serves none.
 * @param failure What the pharmacist reads when the download fails.
 */
export function useClosingDocument(document: ComputedRef<ClosingDocument | null>, name: string, failure: string) {
    const downloading = ref(false);

    const download = async () => {
        const served = document.value;

        if (!served || downloading.value) {
            return;
        }

        downloading.value = true;

        try {
            const blob = await medicationCheckupApi().downloadDocument(served.url);

            fileHelper.downloadBlob(closingDocumentFilename(served, name), blob);
        } catch {
            useToastStore().showError({content: failure});
        } finally {
            downloading.value = false;
        }
    };

    return {downloading, download};
}
